import { raiseErrorToast } from '@celito.clients/utils';
import { PopoverProps } from '@fluentui/react-components';
import { useEffect, useState } from 'react';

import {
  getRecordActivityLogs,
  getWorkflowHistory,
} from '../workflow-history/workflow-actions/services';
import { WizardStateComponentProps } from './wizard-state.model';
import WizardStateView from './wizard-state.view';

const mapStagesToGroups = (
  stageLabels: string[],
  lifecycleStageGroups?: Record<
    string,
    { stages: string[]; displayActively: boolean; displayOrder: number }
  >
): string[] => {
  if (!lifecycleStageGroups) return [];
  const groupNames: string[] = [];
  for (const stageLabel of stageLabels) {
    for (const [groupLabel, group] of Object.entries(lifecycleStageGroups)) {
      if (group.stages.includes(stageLabel) && !groupNames.includes(groupLabel)) {
        groupNames.push(groupLabel);
      }
    }
  }
  return groupNames;
};

export const WizardStateController = (props: WizardStateComponentProps) => {
  const [open, setOpen] = useState(false);
  const [taskGroupNames, setTaskGroupNames] = useState<string[]>([]);
  const handleOpenChange: PopoverProps['onOpenChange'] = (e, data) => {
    // check if the id's dropdown options and no result options are in view port then keep open the workflow history
    // else let event handle the selection
    const target = e.currentTarget as EventTarget & (HTMLElement | Document);

    if (target instanceof HTMLElement || target instanceof Document) {
      const dropdownWithOptions = target.querySelector(
        '#dropdown-with-options'
      );
      const dropdownWithoutOptions = target.querySelector(
        '#dropdown-with-no-results'
      );
      if (dropdownWithOptions || dropdownWithoutOptions) {
        setOpen(true);
      } else {
        setOpen(data.open || false);
      }
    } else {
      setOpen(data.open || false);
    }
  };
  useEffect(() => {
    getTasks();
  }, [props?.recordData]);

  const getTasks = async () => {
    const groupNames: string[] = [];
    try {
      if (props?.objectName && props?.recordName) {
        // Fetch workflow task history
        const taskList = await getWorkflowHistory(
          props.objectName,
          props.recordName,
          props?.objectDefinitions?.isVersioningEnabled
            ? (props?.recordData?.version as string)
            : ''
        );
        if (taskList?.tasks?.length) {
          taskList?.tasks?.forEach((task) => {
            groupNames.push(task?.objectRecordLifecycleGroup);
          });
        }

        // Fetch activity logs as supplementary source for stage traversal
        // This covers lifecycle transitions driven by parent records (e.g., Root Cause, Investigation)
        // where no workflow tasks are created
        const activityLogs = await getRecordActivityLogs(
          props.objectName,
          props.recordName
        );
        if (activityLogs?.length) {
          const stageLabels = activityLogs
            .map((log) => (log['record_status__s'] ?? log['recordStatus']) as string)
            .filter(Boolean);
          const activityGroupNames = mapStagesToGroups(
            stageLabels,
            props?.objectDefinitions?.lifecycleStageGroups
          );
          for (const name of activityGroupNames) {
            if (!groupNames.includes(name)) {
              groupNames.push(name);
            }
          }
        }

        if (groupNames.length) {
          setTaskGroupNames(groupNames);
        }
      }
    } catch (error) {
      raiseErrorToast(error);
    }
  };

  return (
    <WizardStateView
      {...props}
      loading={false}
      handleOpenChange={handleOpenChange}
      open={open}
      taskGroupNames={taskGroupNames}
    />
  );
};
